import React, { useState, useContext, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import {
  formatNumber,
  getFirstTwoDecimals,
} from "../../../../services/utils/formatAndCompare";

import {
  blendColors,
  dangerColor,
  grayColor,
  whiteColor,
} from "../../../../assets/jss/material-dashboard-pro-react";
import AuthContext from "../../../../connections/auth/AuthProvider";

import { getOom } from "../../../../services/utils/formatAndCompare";

const styles = (clientColor) => ({
  plWrapper: {
    position: "relative",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: "5px",
    overflow: "hidden",
  },
  plHeader: {
    color: whiteColor,
    borderRadius: "5px 5px 0 0",
    paddingLeft: "5px",
    textAlign: "left",
    fontSize: "12px",
    paddingTop: "2px",
    paddingBottom: "2px",
    marginBottom: "0",
    marginTop: "0",
    margin: "0",
    backgroundColor: clientColor,
    flexShrink: 0,
    zIndex: 10,
  },
  plName: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    "&.alignRight": {
      textAlign: "right",
      paddingRight: "4px",
    },
    "&:not(.alignRight)": {
      maxWidth: "80%",
    },
  },
  plOuterWrapper: {
    width: "100%",
    padding: "4px 0",
    flex: 1,
    display: "block",
    fontSize: "12px",
    color: grayColor[6],
    whiteSpace: "nowrap",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#c1c1c1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#a1a1a1",
    },
  },
  plLineWrapper: {
    width: "100%",
    minHeight: "24px",
    display: "flex",
    lineHeight: "2",
    "& > div:first-child": {
      paddingLeft: "4px",
    },
    "& > div:last-child": {
      paddingRight: "4px",
    },
    transition: "opacity 0.2s",
    cursor: "pointer",
  },
  plLineDescription: {
    width: "40%",
    textOverflow: "ellipsis",
    "&.hidden": {
      display: "none",
    },
    "&.nameAndValue": {
      width: "77.5%",
    },
    "&.full": {
      width: "100%",
    },
  },
  plValue: {
    width: "20%",
    textAlign: "right",
    "&.negative": {
      color: dangerColor[0],
    },
    "&.currency.expanded": {
      width: "44.444%",
    },
    "&.percentual.expanded": {
      width: "55.555%",
    },
    "&.currency:not(.expanded)": {
      width: "20%",
    },
    "&.percentual:not(.expanded)": {
      width: "25%",
    },
    "&.currency.nameAndValue,&.percentual.nameAndValue": {
      width: "22.5%",
    },
    "&.full": {
      width: "100% !important",
    },
  },
  plNumber: {
    fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace;',
  },
  plVolumeValue: {
    width: "45%",
    "&.expanded": {
      width: "100% !important",
    },
  },
  plSpinner: {
    maxWidth: "20px",
  },
  plPercTotalVolume: {
    fontSize: "10px",
    lineHeight: "12px",
    color: grayColor[0],
    fontStyle: "italic",
  },
  plTableHeader: {
    color: whiteColor,
    backgroundColor: grayColor[6],
  },
  plTotal: {
    backgroundColor: grayColor[8],
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  plSubTotal: {
    backgroundColor: grayColor[13],
    "& > div:first-child": {
      paddingLeft: "12px !important",
    },
  },
  plLevel3: {
    color: grayColor[1],
  },
  highlighted: {
    backgroundColor:
      blendColors(grayColor[8], clientColor, 0.9) + " !important",
  },
  plLoadingDetails: {
    backgroundSize: "200% 100%",
    backgroundImage:
      "linear-gradient(to right, " +
      blendColors(grayColor[8], clientColor, 0.9) +
      " 50%, " +
      blendColors(grayColor[8], clientColor, 0.8) +
      " 50%)",
    "-webkit-transition": "background-position 3s ease-in-out",
    "-moz-transition": "background-position 3s ease-in-out",
    transition: "background-position 3s ease-in-out",
    "&:hover": {
      backgroundPosition: "-100% 0",
    },
  },
  plDrillDown: {
    "& > span:before": {
      fontFamily: "'Font Awesome 5 Free'",
      padding: "0 4px 0 0",
      fontWeight: "900",
      fontSize: "10px",
      cursor: "pointer",
    },
  },
  plDrillDownOpen: {
    "& > span:before": {
      content: "'\\f146'",
    },
  },
  plDrillDownClosed: {
    "& > span:before": {
      content: "'\\f0fe'",
    },
  },
  plIndentL2: {
    paddingLeft: "14px !important",
    "& > span:before": {
      color: grayColor[4],
      content: "'\\2517'",
      padding: "0 3px 0 0",
      fontSize: "10px",
    },
  },
  plIndentL2Parent: {
    paddingLeft: "14px !important",
  },
  faded: {
    opacity: "0.3",
  },
  hidden: {
    display: "none",
  },
});

const FinancialPerformanceTable = (props) => {
  let { userDetails } = useContext(AuthContext);
  const client_styles = () => ({
    ...styles(userDetails.implementation_color),
  });
  const useStyles = makeStyles(client_styles);
  const classes = useStyles();

  const { t, i18n } = useTranslation();

  const [oom, setOom] = useState({
    div: 1e6,
    x: " " + t("oom.m"),
  });
  const [showValueByVolumeDecimals, setShowValueByVolumeDecimals] =
    useState(false);

  const {
    plStructure,
    netRevenueAnchor,
    serveMarginAnchor,
    plName,
    drillDownsOpen,
    setDrillDownsOpen,
    plValues,
    volumeUnit,
    volumeL12M,
    currencyUnit,
    showValueType,
    nameOnly,
    showColumns,
    nameAlignRight,
  } = props;

  const handleDrillDownClick = (meta_name) => {
    if (drillDownsOpen.includes(meta_name)) {
      setDrillDownsOpen(drillDownsOpen.filter((item) => item !== meta_name));
    } else {
      setDrillDownsOpen((drillDownsOpen) => [...drillDownsOpen, meta_name]);
    }
    return false;
  };

  const shouldItemBeVisible = (item) => {
    if (!item.parent) {
      return true;
    }

    let currentParent = item.parent;
    const visitedParents = new Set();

    while (currentParent && !visitedParents.has(currentParent)) {
      visitedParents.add(currentParent);

      if (!drillDownsOpen.includes(currentParent)) {
        return false;
      }

      const parentItem = plStructure.find(
        (structItem) => structItem.meta_name === currentParent,
      );
      currentParent = parentItem ? parentItem.parent : null;
    }

    return true;
  };

  useLayoutEffect(() => {
    let net_revenues = [];
    let serve_margins = [];
    let serve_margins_per_volume = [];
    let median = 0;
    let median_per_volume = 0;

    // Aggregate values from all 3 columns
    Object.values(plValues).forEach((value) => {
      if (value !== undefined) {
        if (Object.keys(value).includes(netRevenueAnchor.current)) {
          net_revenues.push(value[netRevenueAnchor.current]);
        }
        if (Object.keys(value).includes(serveMarginAnchor.current)) {
          serve_margins.push(value[serveMarginAnchor.current]);
          serve_margins_per_volume.push(
            value[serveMarginAnchor.current] / value["volume"],
          );
        }
      }
    });

    if (net_revenues.length > 0) {
      const avg_revenue_and_margin = net_revenues.map(
        (num, index) => (num + serve_margins[index]) / 2,
      );
      const sorted = Array.from(avg_revenue_and_margin).sort((a, b) => a - b);
      const sorted_per_volume = Array.from(serve_margins_per_volume).sort(
        (a, b) => a - b,
      );
      const middle = Math.floor(sorted.length / 2);
      const middle_per_volume = Math.floor(sorted_per_volume.length / 2);
      median =
        sorted.length % 2 === 0
          ? (sorted[middle - 1] + sorted[middle]) / 2
          : sorted[middle];
      median_per_volume =
        sorted_per_volume.length % 2 === 0
          ? (sorted_per_volume[middle_per_volume - 1] +
              sorted_per_volume[middle_per_volume]) /
            2
          : sorted_per_volume[middle_per_volume];
    }
    let { div, x } = getOom(median);
    x = x === "" ? x : " " + t("oom." + x);
    setOom({ div: div, x: x });
    setShowValueByVolumeDecimals(median_per_volume < 100);
  }, [plValues, i18n.language, netRevenueAnchor, serveMarginAnchor]);

  return (
    <div
      className={cx({
        [classes.plWrapper]: true,
      })}
    >
      {/* region P&L Header */}
      {(plName || nameOnly) && (
        <div
          className={cx({
            [classes.plHeader]: true,
          })}
        >
          <div
            className={cx({
              [classes.plName]: true,
              ["alignRight"]: nameAlignRight,
            })}
          >
            {plName}
          </div>
        </div>
      )}
      {/* endregion */}
      {/* region P&L Lines */}
      <div className={classes.plOuterWrapper}>
        {/* region P&L Volume */}
        <div className={classes.plLineWrapper}>
          {showColumns.includes("name") && (
            <div
              className={cx({
                [classes.plLineDescription]: true,
                ["nameOnly"]:
                  showColumns.includes("name") && showColumns.length === 1,
                ["nameAndValue"]:
                  showColumns.includes("name") && showColumns.length === 2,
              })}
            >
              {t("Volume")}
            </div>
          )}
          {(showColumns.includes("val1") ||
            showColumns.includes("val2") ||
            showColumns.includes("val3")) && (
            <>
              {showColumns.includes("val1") && (
                <div
                  className={cx({
                    [classes.plValue]: true,
                    [classes.plNumber]: true,
                    [classes.plVolumeValue]: true,
                    ["expanded"]: !showColumns.includes("name"),
                    ["nameAndValue"]:
                      showColumns.includes("name") && showColumns.length === 2,
                  })}
                >
                  {plValues[1].volume ? (
                    <>
                      {formatNumber(plValues[1].volume)}
                      <span className={classes.plUnit}>
                        {" " + volumeUnit.current}
                      </span>
                    </>
                  ) : (
                    "-"
                  )}
                </div>
              )}
              {showColumns.includes("val2") && (
                <div
                  className={cx({
                    [classes.plValue]: true,
                    [classes.plNumber]: true,
                    [classes.plVolumeValue]: true,
                    ["expanded"]: !showColumns.includes("name"),
                    ["nameAndValue"]:
                      showColumns.includes("name") && showColumns.length === 2,
                  })}
                >
                  {plValues[2].volume ? (
                    <>
                      {formatNumber(plValues[2].volume)}
                      <span className={classes.plUnit}>
                        {" " + volumeUnit.current}
                      </span>
                    </>
                  ) : (
                    "-"
                  )}
                </div>
              )}
              {showColumns.includes("val3") && (
                <div
                  className={cx({
                    [classes.plValue]: true,
                    [classes.plNumber]: true,
                    [classes.plVolumeValue]: true,
                    ["expanded"]: !showColumns.includes("name"),
                    ["nameAndValue"]:
                      showColumns.includes("name") && showColumns.length === 2,
                  })}
                >
                  {plValues[3].volume ? (
                    <>
                      {formatNumber(plValues[3].volume)}
                      <span className={classes.plUnit}>
                        {" " + volumeUnit.current}
                      </span>
                    </>
                  ) : (
                    "-"
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {volumeL12M.current && (
          <div className={classes.plLineWrapper}>
            {showColumns.includes("name") && (
              <div
                className={cx({
                  [classes.plLineDescription]: true,
                  [classes.plPercTotalVolume]: true,
                  ["nameOnly"]:
                    showColumns.includes("name") && showColumns.length === 1,
                  ["nameAndValue"]:
                    showColumns.includes("name") && showColumns.length === 2,
                })}
              >
                {t("pnl.percent_of_total_volume")}
              </div>
            )}
            {(showColumns.includes("val1") ||
              showColumns.includes("val2") ||
              showColumns.includes("val3")) && (
              <>
                {showColumns.includes("val1") && (
                  <div
                    className={cx({
                      [classes.plValue]: true,
                      [classes.plNumber]: true,
                      [classes.plPercTotalVolume]: true,
                      [classes.plVolumeValue]: true,
                      ["expanded"]: !showColumns.includes("name"),
                      ["nameAndValue"]:
                        showColumns.includes("name") &&
                        showColumns.length === 2,
                    })}
                  >
                    {plValues[1].volume && volumeL12M.current
                      ? (
                          (100 * plValues[1].volume) /
                          volumeL12M.current
                        ).toFixed(1) + "%"
                      : "-"}
                  </div>
                )}
                {showColumns.includes("val2") && (
                  <div
                    className={cx({
                      [classes.plValue]: true,
                      [classes.plNumber]: true,
                      [classes.plPercTotalVolume]: true,
                      [classes.plVolumeValue]: true,
                      ["expanded"]: !showColumns.includes("name"),
                      ["nameAndValue"]:
                        showColumns.includes("name") &&
                        showColumns.length === 2,
                    })}
                  >
                    {plValues[2].volume && volumeL12M.current
                      ? (
                          (100 * plValues[2].volume) /
                          volumeL12M.current
                        ).toFixed(1) + "%"
                      : "-"}
                  </div>
                )}
                {showColumns.includes("val3") && (
                  <div
                    className={cx({
                      [classes.plValue]: true,
                      [classes.plNumber]: true,
                      [classes.plPercTotalVolume]: true,
                      [classes.plVolumeValue]: true,
                      ["expanded"]: !showColumns.includes("name"),
                      ["nameAndValue"]:
                        showColumns.includes("name") &&
                        showColumns.length === 2,
                    })}
                  >
                    {plValues[3].volume && volumeL12M.current
                      ? (
                          (100 * plValues[3].volume) /
                          volumeL12M.current
                        ).toFixed(1) + "%"
                      : "-"}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {/* endregion */}
        {/* region P&L Table Header */}
        <div className={classes.plLineWrapper}>
          {showColumns.includes("name") && (
            <div
              className={cx({
                [classes.plLineDescription]: true,
                [classes.plTableHeader]: true,
                ["nameAndValue"]:
                  showColumns.includes("name") && showColumns.length === 2,
                ["full"]: showColumns.length === 1,
              })}
            >
              {"Period"}
            </div>
          )}
          {showColumns.includes("val1") && (
            <div
              className={cx({
                [classes.plValue]: true,
                [classes.plTableHeader]: true,
                ["currency"]: true,
                ["expanded"]:
                  !showColumns.includes("name") && showColumns.length === 2,
                ["nameAndValue"]: showColumns.length === 2,
                ["full"]: showColumns.length === 1,
              })}
            >
              {currencyUnit
                ? showValueType === "value"
                  ? t("rgm.executive.company.financial_performance.value_1")
                  : currencyUnit + "/" + volumeUnit.current
                : "-"}
            </div>
          )}
          {showColumns.includes("val2") && (
            <div
              className={cx({
                [classes.plValue]: true,
                [classes.plTableHeader]: true,
                ["currency"]: true,
                ["expanded"]:
                  !showColumns.includes("name") && showColumns.length === 2,
                ["nameAndValue"]: showColumns.length === 2,
                ["full"]: showColumns.length === 1,
              })}
            >
              {currencyUnit
                ? showValueType === "value"
                  ? t("rgm.executive.company.financial_performance.value_2")
                  : currencyUnit + "/" + volumeUnit.current
                : "-"}
            </div>
          )}
          {showColumns.includes("val3") && (
            <div
              className={cx({
                [classes.plValue]: true,
                [classes.plTableHeader]: true,
                ["currency"]: true,
                ["expanded"]:
                  !showColumns.includes("name") && showColumns.length === 2,
                ["nameAndValue"]: showColumns.length === 2,
                ["full"]: showColumns.length === 1,
              })}
            >
              {currencyUnit
                ? showValueType === "value"
                  ? t("rgm.executive.company.financial_performance.value_3")
                  : currencyUnit + "/" + volumeUnit.current
                : "-"}
            </div>
          )}
        </div>
        {/* endregion */}
        {/* region P&L Financials */}
        <div>
          {plStructure.map((val, key) => {
            return (
              <div
                key={key}
                className={cx({
                  [classes.plLineWrapper]: true,
                  [classes.plTotal]: val.level === 1,
                  [classes.plSubTotal]: val.level === 2,
                  [classes.plLevel3]: val.level === 3,
                  [classes.hidden]: val.parent && !shouldItemBeVisible(val),
                })}
              >
                {showColumns.includes("name") && (
                  <>
                    <div
                      className={cx({
                        [classes.plLineDescription]: true,
                        [classes.plDrillDown]: val.is_parent,
                        [classes.plDrillDownOpen]:
                          val.is_parent &&
                          drillDownsOpen.includes(val.meta_name),
                        [classes.plDrillDownClosed]:
                          val.is_parent &&
                          !drillDownsOpen.includes(val.meta_name),
                        [classes.plIndentL2]: val.level === 3 && !val.is_parent,
                        [classes.plIndentL2Parent]:
                          val.level === 2 && val.is_parent,
                        ["nameAndValue"]: showColumns.length === 2,
                        ["full"]: showColumns.length === 1,
                      })}
                    >
                      {val.is_parent && (
                        <span
                          onClick={() => handleDrillDownClick(val.meta_name)}
                        ></span>
                      )}
                      {val.parent && !val.is_parent && <span></span>}
                      {val.name}
                    </div>
                  </>
                )}
                {showColumns.includes("val1") && (
                  <div
                    className={cx({
                      [classes.plValue]: true,
                      [classes.plNumber]: true,
                      ["currency"]: true,
                      ["negative"]:
                        val.level === 1 &&
                        plValues[1][val.meta_name] &&
                        plValues[1][val.meta_name] < 0,
                      ["expanded"]:
                        !showColumns.includes("name") &&
                        showColumns.length === 2,
                      ["nameAndValue"]: showColumns.length === 2,
                      ["full"]: showColumns.length === 1,
                    })}
                  >
                    {plValues[1][val.meta_name] ? (
                      <span>
                        {showValueType === "value"
                          ? (plValues[1][val.meta_name] / oom.div).toFixed(2)
                          : showValueByVolumeDecimals
                            ? formatNumber(
                                plValues[1][val.meta_name] /
                                  plValues[1]["volume"],
                              ) +
                              "." +
                              getFirstTwoDecimals(
                                plValues[1][val.meta_name] /
                                  plValues[1]["volume"],
                              )
                            : formatNumber(
                                plValues[1][val.meta_name] /
                                  plValues[1]["volume"],
                              )}
                      </span>
                    ) : (
                      "-"
                    )}
                  </div>
                )}
                {showColumns.includes("val2") && (
                  <div
                    className={cx({
                      [classes.plValue]: true,
                      [classes.plNumber]: true,
                      ["currency"]: true,
                      ["negative"]:
                        val.level === 1 &&
                        plValues[2][val.meta_name] &&
                        plValues[2][val.meta_name] < 0,
                      ["expanded"]:
                        !showColumns.includes("name") &&
                        showColumns.length === 2,
                      ["nameAndValue"]: showColumns.length === 2,
                      ["full"]: showColumns.length === 1,
                    })}
                  >
                    {plValues[2][val.meta_name] ? (
                      <span>
                        {showValueType === "value"
                          ? (plValues[2][val.meta_name] / oom.div).toFixed(2)
                          : showValueByVolumeDecimals
                            ? formatNumber(
                                plValues[2][val.meta_name] /
                                  plValues[2]["volume"],
                              ) +
                              "." +
                              getFirstTwoDecimals(
                                plValues[2][val.meta_name] /
                                  plValues[2]["volume"],
                              )
                            : formatNumber(
                                plValues[2][val.meta_name] /
                                  plValues[2]["volume"],
                              )}
                      </span>
                    ) : (
                      "-"
                    )}
                  </div>
                )}
                {showColumns.includes("val3") && (
                  <div
                    className={cx({
                      [classes.plValue]: true,
                      [classes.plNumber]: true,
                      ["currency"]: true,
                      ["negative"]:
                        val.level === 1 &&
                        plValues[3][val.meta_name] &&
                        plValues[3][val.meta_name] < 0,
                      ["expanded"]:
                        !showColumns.includes("name") &&
                        showColumns.length === 2,
                      ["nameAndValue"]: showColumns.length === 2,
                      ["full"]: showColumns.length === 1,
                    })}
                  >
                    {plValues[3][val.meta_name] ? (
                      <span>
                        {showValueType === "value"
                          ? (plValues[3][val.meta_name] / oom.div).toFixed(2)
                          : showValueByVolumeDecimals
                            ? formatNumber(
                                plValues[3][val.meta_name] /
                                  plValues[3]["volume"],
                              ) +
                              "." +
                              getFirstTwoDecimals(
                                plValues[3][val.meta_name] /
                                  plValues[3]["volume"],
                              )
                            : formatNumber(
                                plValues[3][val.meta_name] /
                                  plValues[3]["volume"],
                              )}
                      </span>
                    ) : (
                      "-"
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* endregion */}
      </div>
      {/* endregion */}
    </div>
  );
};

FinancialPerformanceTable.defaultProps = {
  showValueType: "value",
  showColumns: ["name", "val1", "val2", "val3"],
};

FinancialPerformanceTable.propTypes = {
  plStructure: PropTypes.array.isRequired,
  netRevenueAnchor: PropTypes.object.isRequired,
  serveMarginAnchor: PropTypes.object.isRequired,
  plName: PropTypes.string,
  drillDownsOpen: PropTypes.array.isRequired,
  setDrillDownsOpen: PropTypes.func.isRequired,
  plValues: PropTypes.object.isRequired,
  volumeUnit: PropTypes.object.isRequired,
  volumeL12M: PropTypes.object.isRequired,
  currencyUnit: PropTypes.string.isRequired,
  showValueType: PropTypes.string,
  nameOnly: PropTypes.bool,
  showColumns: PropTypes.array,
  nameAlignRight: PropTypes.bool,
};

export default FinancialPerformanceTable;
